import { Check, FileText } from 'lucide-react';
import { useRef, type ReactNode } from 'react';
import { CanonicalFileDropzone } from './CanonicalFileDropzone';

export type CanonicalMetadataItem = { label: string; value: ReactNode };
export function CanonicalEntityHeader({
  eyebrow,
  title,
  subtitle,
  avatar,
  status,
  metadata = [],
  actions,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  avatar?: ReactNode;
  status?: ReactNode;
  metadata?: readonly CanonicalMetadataItem[];
  actions?: ReactNode;
}) {
  return (
    <header className="ui-entity-header">
      <div className="ui-entity-header__identity">
        {avatar ? <div className="ui-entity-header__avatar">{avatar}</div> : null}
        <div>
          {eyebrow ? <span className="ui-structure-eyebrow">{eyebrow}</span> : null}
          <div className="ui-entity-header__title">
            <h1>{title}</h1>
            {status}
          </div>
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
      </div>
      {metadata.length ? (
        <dl className="ui-entity-header__metadata">
          {metadata.map((item) => (
            <div key={item.label}>
              <dt>{item.label}</dt>
              <dd>{item.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {actions ? <div className="ui-entity-header__actions">{actions}</div> : null}
    </header>
  );
}

export function CanonicalMetricGrid({ children }: { children: ReactNode }) {
  return <div className="ui-metric-grid">{children}</div>;
}
export function CanonicalMetricCard({
  label,
  value,
  icon,
  detail,
  trend,
  tone = 'default',
  action,
}: {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  detail?: ReactNode;
  trend?: ReactNode;
  tone?: 'default' | 'success' | 'warning' | 'danger' | 'info';
  action?: ReactNode;
}) {
  return (
    <article className={`ui-metric-card ui-metric-card--${tone}`}>
      {icon ? <span className="ui-metric-card__icon">{icon}</span> : null}
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        {detail ? <small>{detail}</small> : null}
      </div>
      {trend ? <div className="ui-metric-card__trend">{trend}</div> : null}
      {action ? <div className="ui-metric-card__action">{action}</div> : null}
    </article>
  );
}

export type CanonicalStep = {
  id: string;
  label: string;
  description?: string;
  status?: 'pending' | 'current' | 'complete' | 'error';
  disabled?: boolean;
};
export function CanonicalStepper({
  steps,
  currentStepId,
  onStepChange,
  label = 'Progreso',
}: {
  steps: readonly CanonicalStep[];
  currentStepId: string;
  onStepChange?: (id: string) => void;
  label?: string;
}) {
  return (
    <nav className="ui-stepper" aria-label={label}>
      <ol>
        {steps.map((step, index) => {
          const current = step.id === currentStepId;
          const status = step.status ?? (current ? 'current' : 'pending');
          return (
            <li key={step.id} data-status={status}>
              <button
                type="button"
                disabled={step.disabled || !onStepChange}
                aria-current={current ? 'step' : undefined}
                onClick={() => onStepChange?.(step.id)}
              >
                <span>{status === 'complete' ? <Check aria-hidden="true" /> : index + 1}</span>
                <span>
                  <strong>{step.label}</strong>
                  {step.description ? <small>{step.description}</small> : null}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function CanonicalSummaryRail({
  title,
  children,
  actions,
}: {
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <aside className="ui-summary-rail" aria-label={title}>
      <h2>{title}</h2>
      <div className="ui-summary-rail__body">{children}</div>
      {actions ? <div className="ui-summary-rail__actions">{actions}</div> : null}
    </aside>
  );
}

export function CanonicalFilterToolbar({
  children,
  actions,
  label = 'Filtros',
}: {
  children: ReactNode;
  actions?: ReactNode;
  label?: string;
}) {
  return (
    <section className="ui-filter-toolbar" aria-label={label}>
      <div className="ui-filter-toolbar__fields">{children}</div>
      {actions ? <div className="ui-filter-toolbar__actions">{actions}</div> : null}
    </section>
  );
}

export type CanonicalTimelineEvent = {
  id: string;
  title: string;
  date: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  actor?: ReactNode;
  category?: ReactNode;
};
export function CanonicalTimeline({
  events,
  empty = 'No existen eventos registrados.',
  label = 'Línea de tiempo',
}: {
  events: readonly CanonicalTimelineEvent[];
  empty?: ReactNode;
  label?: string;
}) {
  return (
    <section className="ui-timeline" aria-label={label}>
      {events.length ? (
        <ol>
          {events.map((event) => (
            <li key={event.id}>
              <span className="ui-timeline__marker">{event.icon}</span>
              <article>
                <div className="ui-timeline__heading">
                  <strong>{event.title}</strong>
                  <time>{event.date}</time>
                </div>
                {event.description ? <p>{event.description}</p> : null}
                {event.actor || event.category ? (
                  <footer>
                    {event.actor}
                    {event.category}
                  </footer>
                ) : null}
              </article>
            </li>
          ))}
        </ol>
      ) : (
        <div className="ui-timeline__empty">{empty}</div>
      )}
    </section>
  );
}

export function CanonicalCompletion({
  value,
  label = 'Completitud',
  detail,
  tone = 'success',
}: {
  value: number;
  label?: string;
  detail?: ReactNode;
  tone?: 'success' | 'warning' | 'danger' | 'info';
}) {
  const normalized = Math.min(100, Math.max(0, Math.round(value)));
  return (
    <section className={`ui-completion ui-completion--${tone}`} aria-label={label}>
      <div>
        <strong>{label}</strong>
        <span>{normalized}%</span>
      </div>
      <progress max={100} value={normalized}>
        {normalized}%
      </progress>
      {detail ? <p>{detail}</p> : null}
    </section>
  );
}

export type CanonicalSectionTab = { id: string; label: string; count?: number; disabled?: boolean };
export function CanonicalSectionTabs({
  tabs,
  activeId,
  onChange,
  label = 'Secciones',
}: {
  tabs: readonly CanonicalSectionTab[];
  activeId: string;
  onChange: (id: string) => void;
  label?: string;
}) {
  const refs = useRef(new Map<string, HTMLButtonElement>());
  const move = (index: number, direction: number) => {
    const enabled = tabs.filter((tab) => !tab.disabled);
    const current = enabled.findIndex((tab) => tab.id === tabs[index]?.id);
    const next = enabled[(current + direction + enabled.length) % enabled.length];
    if (next) {
      refs.current.get(next.id)?.focus();
      onChange(next.id);
    }
  };
  return (
    <nav className="ui-section-tabs" aria-label={label}>
      <div role="tablist">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            ref={(node) => {
              if (node) refs.current.set(tab.id, node);
              else refs.current.delete(tab.id);
            }}
            type="button"
            role="tab"
            aria-selected={activeId === tab.id}
            tabIndex={activeId === tab.id ? 0 : -1}
            disabled={tab.disabled}
            onClick={() => onChange(tab.id)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') {
                event.preventDefault();
                move(index, 1);
              }
              if (event.key === 'ArrowLeft') {
                event.preventDefault();
                move(index, -1);
              }
            }}
          >
            {tab.label}
            {typeof tab.count === 'number' ? <span>{tab.count}</span> : null}
          </button>
        ))}
      </div>
    </nav>
  );
}

export function CanonicalDocumentField({
  title,
  description,
  required = false,
  status,
  file,
  onFileChange,
  onError,
  disabled = false,
  actions,
}: {
  title: string;
  description?: string;
  required?: boolean;
  status?: ReactNode;
  file: File | null;
  onFileChange: (file: File | null) => void;
  onError?: (message: string) => void;
  disabled?: boolean;
  actions?: ReactNode;
}) {
  return (
    <section className="ui-document-field">
      <header>
        <span className="ui-document-field__icon">
          <FileText aria-hidden="true" />
        </span>
        <div>
          <h3>
            {title}
            {required ? <span aria-label="requerido"> *</span> : null}
          </h3>
          {description ? <p>{description}</p> : null}
        </div>
        {status ? <div>{status}</div> : null}
      </header>
      <CanonicalFileDropzone
        label={title}
        file={file}
        onFileChange={onFileChange}
        {...(onError ? { onError } : {})}
        disabled={disabled}
      />
      {actions ? <footer>{actions}</footer> : null}
    </section>
  );
}

export type CanonicalActivityItem = {
  id: string;
  title: string;
  description?: ReactNode;
  meta?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
};
export function CanonicalActivityList({
  items,
  empty = 'Sin actividad reciente.',
  label = 'Actividad reciente',
}: {
  items: readonly CanonicalActivityItem[];
  empty?: ReactNode;
  label?: string;
}) {
  return (
    <section className="ui-activity-list" aria-label={label}>
      {items.length ? (
        <ul>
          {items.map((item) => (
            <li key={item.id}>
              {item.icon ? <span className="ui-activity-list__icon">{item.icon}</span> : null}
              <div>
                <strong>{item.title}</strong>
                {item.description ? <p>{item.description}</p> : null}
                {item.meta ? <small>{item.meta}</small> : null}
              </div>
              {item.action ? <div className="ui-activity-list__action">{item.action}</div> : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="ui-activity-list__empty">{empty}</p>
      )}
    </section>
  );
}

export function CanonicalDefinitionList({
  items,
  columns = 2,
  label,
}: {
  items: readonly CanonicalMetadataItem[];
  columns?: 1 | 2 | 3 | 4;
  label?: string;
}) {
  return (
    <dl className="ui-definition-list" data-columns={columns} aria-label={label}>
      {items.map((item) => (
        <div key={item.label}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function CanonicalStickyActions({
  children,
  label = 'Acciones del formulario',
}: {
  children: ReactNode;
  label?: string;
}) {
  return (
    <footer className="ui-sticky-actions" aria-label={label}>
      {children}
    </footer>
  );
}
